import React, { useEffect, useState, useCallback } from 'react';
import { Copy, Mail, Pencil, RefreshCw, Trash2, Plus, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Link } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { partnersApi } from './phase567Api';
import { copyText } from './clipboard';
import LongTextCell from '@/components/LongTextCell';
import PhoneInput from './shared/PhoneInput';
import { useAuth } from './AuthContext';

export default function PartnersAdminPage() {
  const { user } = useAuth();
  // Mirrors backend ACTION_KEYS["partners.manage"] -> staff.manage.
  const canManage = user?.is_super_admin || (user?.permissions || []).includes('staff.manage');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // the partner being edited, or null
  const [viewing, setViewing] = useState(null); // the partner whose referrals are open, or null
  const [refs, setRefs] = useState(null); // null = loading
  const blankForm = { name: '', email: '', kind: 'realtor', company: '', phone: '', notes: '' };
  const [form, setForm] = useState(blankForm);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await partnersApi.list({ includeInactive: showInactive }); setItems(d.items || []); }
    catch { toast({ title: 'Load failed' }); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (p) => {
    if (!p.invite_token) {
      toast({ title: 'No link on file', description: 'Make a new link for this partner first' });
      return;
    }
    const link = `${window.location.origin}/partner?token=${p.invite_token}`;
    if (await copyText(link)) toast({ title: 'Partner link copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  const [errors, setErrors] = useState({});
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openNew = () => { setErrors({}); setForm(blankForm); setCreating(true); };
  const openEdit = (p) => {
    setErrors({});
    setForm({ name: p.name || '', email: p.email || '', kind: p.kind || 'realtor', company: p.company || '', phone: p.phone || '', notes: p.notes || '' });
    setEditing(p);
  };
  const closeDialog = () => { setCreating(false); setEditing(null); };

  const submit = async () => {
    if (!validate()) return;
    try {
      const payload = { ...form, name: form.name.trim(), email: form.email.trim() };
      ['company', 'phone', 'notes'].forEach((k) => { payload[k] = (payload[k] || '').trim(); });
      if (editing) {
        await partnersApi.update(editing.partner_id, payload); // blanks clear the field
        toast({ title: 'Partner updated' });
      } else {
        ['company', 'phone', 'notes'].forEach((k) => { if (!payload[k]) delete payload[k]; });
        await partnersApi.create(payload);
        toast({ title: 'Partner added', description: 'Use the mail button to send them their portal link.' });
      }
      closeDialog();
      setForm(blankForm);
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail ? 'Check the fields and try again' : String(e) }); }
  };

  const sendLink = async (p) => {
    try {
      const r = await partnersApi.sendLink(p.partner_id);
      if (r.delivered) toast({ title: 'Portal link sent', description: `Emailed to ${p.email}` });
      else toast({ title: 'Not sent', description: `${p.email} is blocked (${r.blocked}). Copy the link instead.` });
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Copy the link and send it yourself.' });
    }
  };

  const openReferrals = async (p) => {
    setViewing(p); setRefs(null);
    try { const d = await partnersApi.referrals(p.partner_id); setRefs(d.items || []); }
    catch { toast({ title: 'Could not load referrals' }); setViewing(null); }
  };

  const regen = async (p) => {
    if (!(await confirmDialog({ title: 'Regenerate partner token?', description: 'The old partner link will stop working.', confirmText: 'Regenerate' }))) return;
    try { await partnersApi.regenToken(p.partner_id); load(); toast({ title: 'Token regenerated' }); }
    catch { toast({ title: 'Failed' }); }
  };

  const del = async (p) => {
    if (!(await confirmDialog({ title: 'Deactivate this partner?', description: 'Their portal link stops working. You can restore them later.', confirmText: 'Deactivate', destructive: true }))) return;
    try { await partnersApi.del(p.partner_id); load(); }
    catch { toast({ title: 'Failed' }); }
  };

  const restore = async (p) => {
    try { await partnersApi.update(p.partner_id, { active: true }); load(); toast({ title: 'Partner restored' }); }
    catch { toast({ title: 'Failed' }); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="partners-admin-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="partners-title">Referral Partners</h1>
          <p className="text-slate-500 mt-1" data-testid="partners-subtitle">Realtors · Builders · CPAs · Attorneys — each gets a secure co-branded portal.</p>
        </div>
        <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive" />
          Show deactivated
        </label>
        {canManage && (
          <button onClick={openNew} className="btn-gold" data-testid="new-partner-btn"><Plus size={16} /> New Partner</button>
        )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3">Partner</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">Referrals</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-16 text-slate-400">No partners yet. Click "New Partner" to add your first.</td></tr>
                ) : items.map((p) => (
                  <tr key={p.partner_id} className={`border-b border-slate-50 hover:bg-slate-50${p.active === false ? ' opacity-60' : ''}`} data-testid={`partner-${p.partner_id}`}>
                    <td className="px-4 py-4">
                      <LongTextCell id={`partner-name-${p.partner_id}`} text={p.name} lines={2} maxWidth={220} className="font-semibold text-sm text-[#070B5F]" />
                      {p.active === false && <div className="text-xs text-red-600" data-testid={`partner-inactive-${p.partner_id}`}>Deactivated</div>}
                      <LongTextCell id={`partner-company-${p.partner_id}`} text={p.company || '—'} lines={2} maxWidth={220} className="text-xs text-slate-500" />
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{p.email}</td>
                    <td className="px-4 py-4 text-sm capitalize" style={{ color: '#070B5F' }}>{p.kind?.replace('_', ' ')}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      <button onClick={() => openReferrals(p)} aria-label={`View referrals from ${p.name}`} className="underline decoration-dotted hover:text-[#070B5F]" data-testid={`partner-referrals-${p.partner_id}`}>
                        {p.total_referrals || 0} referral(s) · {p.closed_referrals || 0} closed
                      </button>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {canManage && p.active === false && (
                        <button onClick={() => restore(p)} aria-label={`Restore ${p.name}`} className="btn-outline" data-testid={`partner-restore-${p.partner_id}`}>Restore</button>
                      )}
                      {canManage && p.active !== false && (
                      <>
                        <button onClick={() => copyLink(p)} title="Copy portal link" aria-label={`Copy portal link for ${p.name}`} className="p-2 hover:bg-slate-100 rounded-lg">
                          <Copy size={14} style={{ color: '#070B5F' }} />
                        </button>
                        <button onClick={() => openEdit(p)} title="Edit partner" aria-label={`Edit ${p.name}`} className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid={`partner-edit-${p.partner_id}`}>
                          <Pencil size={14} style={{ color: '#070B5F' }} />
                        </button>
                        <button onClick={() => sendLink(p)} title="Send portal link" aria-label={`Send portal link to ${p.name}`} className="p-2 hover:bg-slate-100 rounded-lg ml-1">
                          <Mail size={14} style={{ color: '#070B5F' }} />
                        </button>
                        <button onClick={() => regen(p)} title="Regenerate token" aria-label={`Regenerate token for ${p.name}`} className="p-2 hover:bg-slate-100 rounded-lg ml-1">
                          <RefreshCw size={14} style={{ color: '#070B5F' }} />
                        </button>
                        <button onClick={() => del(p)} title="Deactivate" aria-label={`Deactivate ${p.name}`} className="p-2 hover:bg-slate-100 rounded-lg ml-1">
                          <Trash2 size={14} className="text-red-500" />
                        </button>
                      </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">{editing ? 'Edit Referral Partner' : 'New Referral Partner'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="partner-name" aria-invalid={!!errors.name} />
              {errors.name && <div className="text-xs text-red-600 mt-1" data-testid="partner-name-error">{errors.name}</div>}</div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="partner-email" aria-invalid={!!errors.email} />
              {errors.email && <div className="text-xs text-red-600 mt-1" data-testid="partner-email-error">{errors.email}</div>}</div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" data-testid="partner-company" /></div>
            <div><Label>Phone</Label><PhoneInput value={form.phone} onChange={(d) => setForm({ ...form, phone: d })} className="mt-1" data-testid="partner-phone" /></div>
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtor">Realtor</SelectItem>
                  <SelectItem value="builder">Builder</SelectItem>
                  <SelectItem value="cpa">CPA</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="financial_planner">Financial Planner</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" data-testid="partner-notes" /></div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={closeDialog} className="btn-outline">Cancel</button>
              <button onClick={submit} className="btn-gold" data-testid="partner-save-btn">Save</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Referrals from {viewing?.name}</DialogTitle></DialogHeader>
          {refs === null ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading...</div>
          ) : refs.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="partner-referrals-empty">No referrals from this partner yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto" data-testid="partner-referrals-list">
              {refs.map((r) => (
                <li key={r.referral_id} className="py-3 flex items-center justify-between gap-3" data-testid={`partner-referral-${r.referral_id}`}>
                  <div className="min-w-0">
                    {r.contact_id
                      ? <Link to={`/portal/contacts/${r.contact_id}`} className="text-sm font-medium text-[#070B5F] underline">{r.borrower_first_name} {r.borrower_last_name}</Link>
                      : <span className="text-sm font-medium">{r.borrower_first_name} {r.borrower_last_name}</span>}
                    <div className="text-xs text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}{r.loan_purpose ? ` · ${r.loan_purpose}` : ''}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 whitespace-nowrap" data-testid={`partner-referral-stage-${r.referral_id}`}>{r.stage_label}</span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
